import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {TimeZone} from 'timezones-list';
import type {RegistrationMode, EventWizardProgressStep} from '@Components/event-wizard/event-wizard.types';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';

export interface PreviewProps {
  isMobileScreenSize?: boolean;
}

export const EVENT_PAGE_MAIN_CONTAINER_ID = 'event-page';

export const PREVIEW_IFRAME_ID = 'previewIframeID';

// Cover Photo
export const COVER_PHOTO_CONTAINER_ID = 'coverPhotoContainerID';
export const COVER_PHOTO_ID = 'coverPhotoID';
export const COVER_PHOTO_PLACEHOLDER_ICON_ID = 'coverPhotoIconPlaceholderID';
export const COVER_PHOTO_BACKGROUND_ID = 'coverPhotoBgID';

// Title
export const TITLE_ID = 'titleID';

// Description
export const DESCRIPTION_ID = 'descriptionID';

// Venue Summary
export const VENUE_SUMMARY_TITLE = 'venueSummaryTitle';
export const VENUE_SUMMARY_SUBTITLE = 'venueSummarySubtitle';
export const VENUE_ICON_EMPTY_ID = 'venueIconEmptyID';
export const VENUE_ICON_MAP_ID = 'venueIconMapID';
export const VENUE_ICON_GOOGLE_MEET_ID = 'venueIconGoogleMeetID';
export const VENUE_ICON_ZOOM_ID = 'venueIconZoomID';
export const VENUE_ICON_ONLINE_ID = 'venueIconOnlineID';
export const VENUE_ICON_ONLINE_NON_URL_ID = 'venueIconOnlineNonURLID';

// Venue
export const VENUE_MAP_ID = 'venueMapID';
export const VENUE_MAP_LOCATION_ID = 'venueMapLocationID';
export const VENUE_MAP_NAME_ID = 'venueMapNameID';
export const VENUE_MAP_ADDRESS_ID = 'venueMapAddressID';
export const VENUE_MAP_ADDRESS_DIRECTIONS_ID = 'venueMapAddressDirectionsID';

export const VENUE_ONLINE_ID = 'venueOnlineID';
export const VENUE_ONLINE_NON_URL_ID = 'venueOnlineNonURLID';
export const VENUE_ONLINE_NON_URL_TEXT_ID = 'venueOnlineNonURLTextID';
export const VENUE_ONLINE_GOOGLE_MEET_ID = 'venueOnlineGoogleMeetID';
export const VENUE_ONLINE_ZOOM_ID = 'venueOnlineZoomID';

// Date Time
export const DATE_TIME_EMPTY_ID = 'dateTimeEmptyID';
export const DATE_TIME_SINGLE_DAY_ID = 'dateTimeSingleDayID';
export const DATE_TIME_MULTIDAY_ID = 'dateTimeMultidayID';
export const DATE_TIME_RECURRING_DAILY_ID = 'dateTimeRecurDailyID';
export const DATE_TIME_RECURRING_WEEKLY_SINGLE_ID = 'dateTimeRecurWeeklySingleID';
export const DATE_TIME_RECURRING_WEEKLY_MULTI_ID = 'dateTimeRecurWeeklyMultiID';
export const DATE_TIME_RECURRING_MONTHLY_ID = 'dateTimeRecurMonthlyID';

export const DATE_TIME_ICON_START_MONTH_ID = 'dateTimeIconStartMonthID';
export const DATE_TIME_ICON_START_DATE_ID = 'dateTimeIconStartDateID';
export const DATE_TIME_START_TITLE_ID = 'dateTimeStartTitleID';
export const DATE_TIME_MOBILE_START_TITLE_ID = 'dateTimeMobileStartTitleID';
export const DATE_TIME_START_SUBTITLE_ID = 'dateTimeStartSubtitleID';
export const DATE_TIME_END_TITLE_ID = 'dateTimeEndTitleID';
export const DATE_TIME_MOBILE_END_TITLE_ID = 'dateTimeMobileEndTitleID';
export const DATE_TIME_END_SUBTITLE_ID = 'dateTimeEndSubtitleID';
export const DATE_TIME_TIMEZONE_ID = 'dateTimeTimeZoneID';

// Countdown
export const CUSTOM_EVENT_COUNTDOWN_WIZARD_UPDATED = 'countdown_updated_wizard';
export const COUNTDOWN_ID = 'countdownID';

// Organiser
export const ORGANISER_PROFILE_PICTURE = 'organiserProfilePictureID';
export const ORGANISER_NAME = 'organiserNameID';
export const ORGANISER_EMAIL = 'organiserEmailID';
export const ORGANISER_PHONE = 'organiserPhoneID';
export const ORGANISER_SOCIAL_CONTAINER = 'organiserSocialContainerID';
export const ORGANISER_SOCIAL_FACEBOOK = 'organiserSocialFacebookID';
export const ORGANISER_SOCIAL_INSTAGRAM = 'organiserSocialInstagramID';
export const ORGANISER_SOCIAL_LINKEDIN = 'organiserSocialLinkedinID';
export const ORGANISER_SOCIAL_X = 'organiserSocialXID';
export const ORGANISER_SOCIAL_PINTEREST = 'organiserSocialPinterestID';
export const ORGANISER_SOCIAL_YOUTUBE = 'organiserSocialYoutubeID';
export const ORGANISER_SOCIAL_TIKTOK = 'organiserSocialTiktokID';

// Registration form
export const REGISTRATION_CTA_ID = 'registrationCTAID';
export const CUSTOM_EVENT_WIZARD_CHECKOUT_SYNC = 'wizard_checkout_preview_sync';

export interface WizardCheckoutPreviewSyncArgs {
  config: TicketingConfig;
  mode: RegistrationMode;
  step: EventWizardProgressStep;
  eventTitle: string;
  startDateTime: Date;
  coverPhotoUrl?: string;
  /** Server-resolved fee rates, or null until they load — the preview total falls back to the ticket price meanwhile. */
  ticketingFees: TicketingFeeParams | null;
}

// Watermark
export const WATERMARK_ID = 'watermarkID';

// Styling
export const STYLE_LIGHTER_BG_COLOR_ID = 'styleLighterBgColorID';
export const STYLE_LIGHT_BG_COLOR_ID = 'styleLightBgColorID';
export const STYLE_COLOR_ID = 'styleColorID';
export const STYLE_DARK_TEXT_COLOR_ID = 'styleDarkColorID';

export interface EventPreviewDateTime {
  startDateTime: Date;
  endDateTime: Date;
  recurringPattern?: RecurringEvent;
  timeZone: TimeZone;
}
